/**
 * Demo datasets for the BioAvail app (Stage 10 / Sub-stage 5a).
 *
 * ## Dataset count justification
 *
 * The engine (`src/engine/compute.ts`) branches across several orthogonal
 * axes. Each branch produces a distinct result shape that should be
 * exercised by at least one dataset:
 *
 *   1. `CalculationMode` — Absolute vs Relative (different formula label,
 *      different warning policy for F > 1).
 *   2. `AucInterval` — ZeroToT (no λ_z), ZeroToInf (λ_z + extrapolation),
 *      Interval (partial AUC with boundary interpolation).
 *   3. Per-arm `usesDirectAuc` — bypasses trapezoidal + λ_z entirely and
 *      uses `directAuc` directly.
 *   4. Presence of an IV arm — toggles MRT/MAT computation in the PK layer.
 *
 * The Stage 10 spec requires at minimum: minimal/simple, typical
 * mid-complexity, and maximal/stress cases, plus additional datasets for
 * any branching paths not covered by those three. Mapping the branches
 * above to concrete datasets:
 *
 *   - Minimal Absolute F with AUC(0-t)            → branches 1 (absolute) + 2 (ZeroToT) + 4 (IV)
 *   - Typical Mid-Complexity Absolute F (AUC 0-∞) → branches 1 (absolute) + 2 (ZeroToInf) + 4 (IV + λ_z + MRT/MAT)
 *   - Relative F (oral vs oral, unequal doses)    → branch 1 (relative); no IV so MRT/MAT skipped
 *   - Maximal/Stress (dense sampling, AUC 0-∞)    → exercises the engine at high point count
 *   - Partial AUC Interval                         → branch 2 (Interval + boundary interpolation)
 *   - Direct-AUC arm                               → branch 3 (direct AUC bypass)
 *
 * N = 6 datasets. This is the minimal set that covers every engine branch
 * at least once while still providing the three baseline complexity
 * tiers. Adding more would duplicate coverage without exercising new
 * logic.
 *
 * ## Snapshot semantics
 *
 * Each dataset is a complete `Study` snapshot — the meaningful state that
 * the engine consumes. Full store/UI state (settings, audit trail, UI
 * flags) does not vary between datasets and is left to the load mechanism
 * to populate. This mirrors the existing `src/data/examples/` pattern.
 */
import type { Study, ConcentrationTimePoint } from '@bioavail/shared-types';
import { createArm, createStudy } from './factory';
import {
  CalculationMode as CM,
  AucInterval as AI,
  RouteOfAdministration as ROA,
  ArmRole as AR,
  AnalyteType as AT,
  ConcentrationUnit as CU,
  TimeUnit as TU,
  DoseUnit as DU,
  TrapezoidalRule as TR,
  BlqRule as BR,
} from '@bioavail/shared-types';

export interface DemoDatasetEntry {
  id: string;
  name: string;
  description: string;
  build: () => Study;
}

// ── 1. Minimal Absolute F (AUC 0-t, no extrapolation) ─────────────────
// Smallest valid dataset: 4 points per arm (≥ 2 required by trapezoidal),
// AUC(0-t) interval so λ_z is never invoked. Exercises the simplest
// happy-path through the engine.
function minimalAbsoluteF(): Study {
  const oral: ConcentrationTimePoint[] = [
    { time: 1, concentration: 200, isBlq: false },
    { time: 2, concentration: 350, isBlq: false },
    { time: 4, concentration: 220, isBlq: false },
    { time: 8, concentration: 60, isBlq: false },
  ];
  const iv: ConcentrationTimePoint[] = [
    { time: 0.5, concentration: 1200, isBlq: false },
    { time: 1, concentration: 700, isBlq: false },
    { time: 2, concentration: 320, isBlq: false },
    { time: 4, concentration: 90, isBlq: false },
  ];
  return createStudy({
    name: 'Demo — Minimal Absolute F (AUC 0-t)',
    mode: CM.Absolute,
    aucInterval: AI.ZeroToT,
    arms: [
      createArm({
        id: 'arm-test',
        label: 'Oral 100 mg',
        route: ROA.Oral,
        role: AR.Test,
        dose: 100,
        doseUnit: DU.Mg,
        concentrationUnit: CU.NgPerMl,
        timeUnit: TU.Hour,
        trapezoidalRule: TR.LinearUpLogDown,
        blqRule: BR.M1,
        dataPoints: oral,
      }),
      createArm({
        id: 'arm-ref',
        label: 'IV 50 mg',
        route: ROA.IV,
        role: AR.Reference,
        dose: 50,
        doseUnit: DU.Mg,
        concentrationUnit: CU.NgPerMl,
        timeUnit: TU.Hour,
        trapezoidalRule: TR.LinearUpLogDown,
        blqRule: BR.M1,
        dataPoints: iv,
      }),
    ],
  });
}

// ── 2. Typical Mid-Complexity Absolute F (AUC 0-∞) ────────────────────
// Full concentration-time profile with a clear terminal log-linear phase.
// Exercises λ_z auto-detection, extrapolation, and (because an IV arm is
// present) MRT/MAT in the PK layer.
function typicalAbsoluteF(): Study {
  const oral: ConcentrationTimePoint[] = [
    { time: 0.5, concentration: 60, isBlq: false },
    { time: 1, concentration: 320, isBlq: false },
    { time: 2, concentration: 540, isBlq: false },
    { time: 4, concentration: 380, isBlq: false },
    { time: 6, concentration: 215, isBlq: false },
    { time: 8, concentration: 120, isBlq: false },
    { time: 12, concentration: 35, isBlq: false },
    { time: 16, concentration: 10.2, isBlq: false },
    { time: 24, concentration: 0.9, isBlq: false },
  ];
  const iv: ConcentrationTimePoint[] = [
    { time: 0.25, concentration: 1820, isBlq: false },
    { time: 0.5, concentration: 1440, isBlq: false },
    { time: 1, concentration: 970, isBlq: false },
    { time: 2, concentration: 540, isBlq: false },
    { time: 4, concentration: 185, isBlq: false },
    { time: 6, concentration: 64, isBlq: false },
    { time: 8, concentration: 22, isBlq: false },
    { time: 12, concentration: 2.6, isBlq: false },
    { time: 16, concentration: 0.32, isBlq: false },
  ];
  return createStudy({
    name: 'Demo — Typical Absolute F (AUC 0-∞, IV reference)',
    mode: CM.Absolute,
    aucInterval: AI.ZeroToInf,
    arms: [
      createArm({
        id: 'arm-test',
        label: 'Oral 200 mg tablet',
        route: ROA.Oral,
        role: AR.Test,
        dose: 200,
        doseUnit: DU.Mg,
        concentrationUnit: CU.NgPerMl,
        timeUnit: TU.Hour,
        trapezoidalRule: TR.LinearUpLogDown,
        blqRule: BR.M1,
        dataPoints: oral,
      }),
      createArm({
        id: 'arm-ref',
        label: 'IV 100 mg infusion',
        route: ROA.IV,
        role: AR.Reference,
        dose: 100,
        doseUnit: DU.Mg,
        concentrationUnit: CU.NgPerMl,
        timeUnit: TU.Hour,
        trapezoidalRule: TR.LinearUpLogDown,
        blqRule: BR.M1,
        dataPoints: iv,
      }),
    ],
  });
}

// ── 3. Relative F (oral vs oral, unequal doses, no IV) ────────────────
// Relative mode with two oral arms and unequal doses. Exercises the
// dose-normalization step in the formula breakdown. With no IV arm, the
// MRT/MAT branch is skipped — covering that absence as a valid path.
function relativeFOralVsOral(): Study {
  const test: ConcentrationTimePoint[] = [
    { time: 0.5, concentration: 110, isBlq: false },
    { time: 1, concentration: 275, isBlq: false },
    { time: 2, concentration: 360, isBlq: false },
    { time: 4, concentration: 240, isBlq: false },
    { time: 6, concentration: 140, isBlq: false },
    { time: 8, concentration: 78, isBlq: false },
    { time: 12, concentration: 22, isBlq: false },
    { time: 16, concentration: 5.6, isBlq: false },
    { time: 24, concentration: 0.35, isBlq: false },
  ];
  const ref: ConcentrationTimePoint[] = [
    { time: 0.5, concentration: 65, isBlq: false },
    { time: 1, concentration: 160, isBlq: false },
    { time: 2, concentration: 205, isBlq: false },
    { time: 4, concentration: 138, isBlq: false },
    { time: 6, concentration: 80, isBlq: false },
    { time: 8, concentration: 44, isBlq: false },
    { time: 12, concentration: 12.2, isBlq: false },
    { time: 16, concentration: 3.1, isBlq: false },
    { time: 24, concentration: 0.18, isBlq: false },
  ];
  return createStudy({
    name: 'Demo — Relative F (Tablet 50 mg vs Capsule 25 mg)',
    mode: CM.Relative,
    aucInterval: AI.ZeroToInf,
    arms: [
      createArm({
        id: 'arm-test',
        label: 'Test (Tablet 50 mg)',
        route: ROA.Oral,
        role: AR.Test,
        dose: 50,
        doseUnit: DU.Mg,
        analyteType: AT.Parent,
        concentrationUnit: CU.NgPerMl,
        timeUnit: TU.Hour,
        trapezoidalRule: TR.LinearUpLogDown,
        blqRule: BR.M1,
        dataPoints: test,
      }),
      createArm({
        id: 'arm-ref',
        label: 'Reference (Capsule 25 mg)',
        route: ROA.Oral,
        role: AR.Reference,
        dose: 25,
        doseUnit: DU.Mg,
        analyteType: AT.Parent,
        concentrationUnit: CU.NgPerMl,
        timeUnit: TU.Hour,
        trapezoidalRule: TR.LinearUpLogDown,
        blqRule: BR.M1,
        dataPoints: ref,
      }),
    ],
  });
}

// ── 4. Maximal / Stress (dense sampling, AUC 0-∞) ─────────────────────
// 16 points per arm with frequent early sampling and a long terminal
// phase. Exercises the engine at the upper end of realistic input sizes
// to verify the trapezoidal integrator, λ_z walk-back, and PK parameter
// derivation all remain numerically stable.
function maximalStress(): Study {
  const oral: ConcentrationTimePoint[] = [
    { time: 0.25, concentration: 12, isBlq: false },
    { time: 0.5, concentration: 95, isBlq: false },
    { time: 0.75, concentration: 240, isBlq: false },
    { time: 1, concentration: 410, isBlq: false },
    { time: 1.5, concentration: 560, isBlq: false },
    { time: 2, concentration: 620, isBlq: false },
    { time: 3, concentration: 540, isBlq: false },
    { time: 4, concentration: 420, isBlq: false },
    { time: 6, concentration: 245, isBlq: false },
    { time: 8, concentration: 142, isBlq: false },
    { time: 12, concentration: 48, isBlq: false },
    { time: 16, concentration: 16, isBlq: false },
    { time: 24, concentration: 1.8, isBlq: false },
    { time: 36, concentration: 0.22, isBlq: false },
    { time: 48, concentration: 0.05, isBlq: false },
  ];
  const iv: ConcentrationTimePoint[] = [
    { time: 0.125, concentration: 2400, isBlq: false },
    { time: 0.25, concentration: 1950, isBlq: false },
    { time: 0.5, concentration: 1480, isBlq: false },
    { time: 0.75, concentration: 1140, isBlq: false },
    { time: 1, concentration: 880, isBlq: false },
    { time: 1.5, concentration: 560, isBlq: false },
    { time: 2, concentration: 360, isBlq: false },
    { time: 3, concentration: 165, isBlq: false },
    { time: 4, concentration: 78, isBlq: false },
    { time: 6, concentration: 18, isBlq: false },
    { time: 8, concentration: 4.4, isBlq: false },
    { time: 12, concentration: 0.6, isBlq: false },
    { time: 16, concentration: 0.09, isBlq: false },
    { time: 24, concentration: 0.02, isBlq: false },
  ];
  return createStudy({
    name: 'Demo — Dense-Sampling Stress (AUC 0-∞)',
    mode: CM.Absolute,
    aucInterval: AI.ZeroToInf,
    arms: [
      createArm({
        id: 'arm-test',
        label: 'Oral 250 mg (dense)',
        route: ROA.Oral,
        role: AR.Test,
        dose: 250,
        doseUnit: DU.Mg,
        concentrationUnit: CU.NgPerMl,
        timeUnit: TU.Hour,
        trapezoidalRule: TR.LinearUpLogDown,
        blqRule: BR.M1,
        dataPoints: oral,
      }),
      createArm({
        id: 'arm-ref',
        label: 'IV 100 mg (dense)',
        route: ROA.IV,
        role: AR.Reference,
        dose: 100,
        doseUnit: DU.Mg,
        concentrationUnit: CU.NgPerMl,
        timeUnit: TU.Hour,
        trapezoidalRule: TR.LinearUpLogDown,
        blqRule: BR.M1,
        dataPoints: iv,
      }),
    ],
  });
}

// ── 5. Partial AUC Interval ───────────────────────────────────────────
// Two oral arms with AUC interval 0–8h. Exercises the partial-AUC branch
// in `compute.ts` including boundary interpolation.
function partialAucInterval(): Study {
  const test: ConcentrationTimePoint[] = [
    { time: 0.5, concentration: 80, isBlq: false },
    { time: 1, concentration: 240, isBlq: false },
    { time: 2, concentration: 360, isBlq: false },
    { time: 4, concentration: 280, isBlq: false },
    { time: 6, concentration: 175, isBlq: false },
    { time: 8, concentration: 105, isBlq: false },
    { time: 12, concentration: 35, isBlq: false },
    { time: 16, concentration: 10, isBlq: false },
  ];
  const ref: ConcentrationTimePoint[] = [
    { time: 0.5, concentration: 95, isBlq: false },
    { time: 1, concentration: 285, isBlq: false },
    { time: 2, concentration: 410, isBlq: false },
    { time: 4, concentration: 320, isBlq: false },
    { time: 6, concentration: 200, isBlq: false },
    { time: 8, concentration: 118, isBlq: false },
    { time: 12, concentration: 40, isBlq: false },
    { time: 16, concentration: 11.5, isBlq: false },
  ];
  return createStudy({
    name: 'Demo — Partial AUC (Interval 0–8 h)',
    mode: CM.Relative,
    aucInterval: AI.Interval,
    partialAucWindow: { start: 0, end: 8 },
    arms: [
      createArm({
        id: 'arm-test',
        label: 'Test formulation (100 mg)',
        route: ROA.Oral,
        role: AR.Test,
        dose: 100,
        doseUnit: DU.Mg,
        concentrationUnit: CU.NgPerMl,
        timeUnit: TU.Hour,
        trapezoidalRule: TR.LinearUpLogDown,
        blqRule: BR.M1,
        dataPoints: test,
      }),
      createArm({
        id: 'arm-ref',
        label: 'Reference formulation (100 mg)',
        route: ROA.Oral,
        role: AR.Reference,
        dose: 100,
        doseUnit: DU.Mg,
        concentrationUnit: CU.NgPerMl,
        timeUnit: TU.Hour,
        trapezoidalRule: TR.LinearUpLogDown,
        blqRule: BR.M1,
        dataPoints: ref,
      }),
    ],
  });
}

// ── 6. Direct-AUC arm ─────────────────────────────────────────────────
// Both arms use `directAuc` (pre-computed AUC). Exercises the direct-AUC
// branch which bypasses trapezoidal integration and λ_z estimation.
function directAucArms(): Study {
  return createStudy({
    name: 'Demo — Direct AUC (pre-computed areas)',
    mode: CM.Absolute,
    aucInterval: AI.ZeroToInf,
    arms: [
      createArm({
        id: 'arm-test',
        label: 'Oral test (direct AUC)',
        route: ROA.Oral,
        role: AR.Test,
        dose: 200,
        doseUnit: DU.Mg,
        concentrationUnit: CU.NgPerMl,
        timeUnit: TU.Hour,
        usesDirectAuc: true,
        directAuc: 4200,
        dataPoints: [],
      }),
      createArm({
        id: 'arm-ref',
        label: 'IV reference (direct AUC)',
        route: ROA.IV,
        role: AR.Reference,
        dose: 100,
        doseUnit: DU.Mg,
        concentrationUnit: CU.NgPerMl,
        timeUnit: TU.Hour,
        usesDirectAuc: true,
        directAuc: 5600,
        dataPoints: [],
      }),
    ],
  });
}

export const DEMO_DATASETS: DemoDatasetEntry[] = [
  {
    id: 'minimal-absolute-f',
    name: 'Minimal Absolute F',
    description:
      'Smallest valid study: 4 points per arm, AUC(0-t). Simplest path through the engine — no λ_z, no extrapolation.',
    build: minimalAbsoluteF,
  },
  {
    id: 'typical-absolute-f',
    name: 'Typical Absolute F',
    description:
      'Full concentration-time profile with IV reference, AUC(0-∞). Exercises λ_z auto-detection, extrapolation, and MRT/MAT.',
    build: typicalAbsoluteF,
  },
  {
    id: 'relative-f-oral',
    name: 'Relative F (oral vs oral)',
    description:
      'Two oral arms with unequal doses — relative mode, dose-normalization step, no IV arm (MRT/MAT skipped).',
    build: relativeFOralVsOral,
  },
  {
    id: 'dense-sampling-stress',
    name: 'Dense-Sampling Stress',
    description:
      'Maximal point count (15+ per arm) with long terminal phase. Numerical stability check for trapezoidal + λ_z.',
    build: maximalStress,
  },
  {
    id: 'partial-auc-interval',
    name: 'Partial AUC (Interval)',
    description:
      'AUC over a custom 0–8 h window. Exercises partial-AUC branch with boundary interpolation.',
    build: partialAucInterval,
  },
  {
    id: 'direct-auc',
    name: 'Direct AUC arms',
    description:
      'Both arms use pre-computed AUC values. Bypasses trapezoidal integration and λ_z estimation.',
    build: directAucArms,
  },
];

export function getDemoDataset(id: string): DemoDatasetEntry | undefined {
  return DEMO_DATASETS.find((d) => d.id === id);
}
